import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {
    Badge,
    Box,
    Button,
    Flex,
    FormControl,
    FormLabel,
    Grid,
    HStack,
    Icon,
    IconButton,
    Input,
    InputGroup,
    InputLeftElement,
    Modal,
    ModalBody,
    ModalCloseButton,
    ModalContent,
    ModalFooter,
    ModalHeader,
    ModalOverlay,
    Select,
    SimpleGrid,
    Slider,
    SliderFilledTrack,
    SliderThumb,
    SliderTrack,
    Stack,
    Stat,
    StatLabel,
    StatNumber,
    Switch,
    Text,
    keyframes,
    useColorModeValue,
    useDisclosure,
    useToast,
} from "@chakra-ui/react";
import {FaSearch} from "react-icons/fa";
import {
    MdAdd,
    MdDelete,
    MdLibraryMusic,
    MdMusicNote,
    MdPause,
    MdPlayArrow,
    MdUploadFile,
} from "react-icons/md";
import {fileUpload} from "../../../Services/FileService";
import {deleteBellMusic, saveBellMusic, searchBellMusic} from "./Services";

const MUSIC_TYPES = [
    {value: "GENERAL", label: "General Bell"},
    {value: "PERIOD", label: "Period Bell"},
    {value: "BREAK", label: "Break Bell"},
    {value: "ASSEMBLY", label: "Assembly"},
    {value: "PRAYER", label: "Prayer"},
    {value: "NATIONAL_ANTHEM", label: "National Anthem"},
    {value: "EMERGENCY", label: "Emergency / Alarm"},
    {value: "CUSTOM", label: "Custom"},
];

const typeLabel = (value) => MUSIC_TYPES.find((item) => item.value === value)?.label || value || "—";

// Normalise whatever shape the search endpoint returns into a flat array.
const extractList = (data) => {
    if (Array.isArray(data)) {
        return data;
    }
    if (Array.isArray(data?.content)) {
        return data.content;
    }
    if (Array.isArray(data?.data)) {
        return data.data;
    }
    return [];
};

const trackUrl = (track) => track?.url || track?.uri || track?.fileUrl || track?.bell || "";
const trackName = (track) => track?.name || track?.fileName || track?.label || "Untitled";

const bars = keyframes`
    0%, 100% { transform: scaleY(0.4); }
    50%      { transform: scaleY(1); }
`;

/* --------------------- animated equalizer --------------------- */

function Equalizer({active}) {
    return (
        <HStack spacing="3px" h="20px" align="flex-end">
            {[0, 1, 2, 3].map((i) => (
                <Box
                    key={i}
                    w="3px"
                    h="100%"
                    borderRadius="full"
                    bg={active ? "teal.400" : "gray.300"}
                    transformOrigin="bottom"
                    transform={active ? undefined : "scaleY(0.4)"}
                    animation={active ? `${bars} 0.9s ease-in-out ${i * 0.12}s infinite` : undefined}
                />
            ))}
        </HStack>
    );
}

/* ------------------------ add-music modal --------------------- */

function AddMusicModal({isOpen, onClose, onSaved}) {
    const toast = useToast();
    const [form, setForm] = useState({name: "", type: "GENERAL", enabled: true});
    const [objectKey, setObjectKey] = useState("");
    const [previewUrl, setPreviewUrl] = useState("");
    const [fileName, setFileName] = useState("");
    const [uploading, setUploading] = useState(false);
    const [saving, setSaving] = useState(false);

    const reset = useCallback(() => {
        setForm({name: "", type: "GENERAL", enabled: true});
        setObjectKey("");
        setPreviewUrl((prev) => {
            if (prev) URL.revokeObjectURL(prev);
            return "";
        });
        setFileName("");
        setUploading(false);
        setSaving(false);
    }, []);

    useEffect(() => {
        if (!isOpen) {
            reset();
        }
    }, [isOpen, reset]);

    const handleFile = async (event) => {
        const file = event.target.files?.[0];
        event.target.value = "";
        if (!file) {
            return;
        }
        setFileName(file.name);
        setPreviewUrl((prev) => {
            if (prev) URL.revokeObjectURL(prev);
            return URL.createObjectURL(file);
        });
        if (!form.name) {
            setForm((prev) => ({...prev, name: file.name.replace(/\.[^.]+$/, "")}));
        }

        setUploading(true);
        try {
            const key = await fileUpload(file);
            if (key) {
                setObjectKey(typeof key === "string" ? key : key?.url || key?.key || key?.fileName || "");
                toast({title: "File uploaded", status: "success", duration: 2000, isClosable: true});
            } else {
                throw new Error("Empty upload response");
            }
        } catch (e) {
            toast({title: "Upload failed", description: "Could not upload the audio file.", status: "error", duration: 4000, isClosable: true});
        } finally {
            setUploading(false);
        }
    };

    const handleSave = async () => {
        if (!form.name.trim()) {
            toast({title: "Enter a name", status: "warning", duration: 2500, isClosable: true});
            return;
        }
        if (!objectKey) {
            toast({title: "Upload an audio file first", status: "warning", duration: 2500, isClosable: true});
            return;
        }
        setSaving(true);
        try {
            const request = {
                bellList: [{name: form.name.trim(), url: objectKey, type: form.type, enabled: form.enabled}],
            };
            const response = await saveBellMusic(request);
            if (response.status === 201 || response.status === 200) {
                toast({title: "Music added to library", status: "success", duration: 3000, isClosable: true});
                onSaved();
                onClose();
            } else {
                throw new Error("Unexpected status");
            }
        } catch (e) {
            toast({title: "Could not save", description: "Saving the bell music failed.", status: "error", duration: 4000, isClosable: true});
        } finally {
            setSaving(false);
        }
    };

    return (
        <Modal isOpen={isOpen} onClose={onClose} size="lg">
            <ModalOverlay/>
            <ModalContent>
                <ModalHeader>Add Bell Music</ModalHeader>
                <ModalCloseButton/>
                <ModalBody>
                    <Stack spacing={4}>
                        <FormControl isRequired>
                            <FormLabel>Music name</FormLabel>
                            <Input
                                value={form.name}
                                onChange={(e) => setForm((prev) => ({...prev, name: e.target.value}))}
                                placeholder="e.g. Period change bell"
                            />
                        </FormControl>

                        <FormControl>
                            <FormLabel>Type</FormLabel>
                            <Select value={form.type} onChange={(e) => setForm((prev) => ({...prev, type: e.target.value}))}>
                                {MUSIC_TYPES.map((item) => (
                                    <option key={item.value} value={item.value}>{item.label}</option>
                                ))}
                            </Select>
                        </FormControl>

                        <FormControl>
                            <FormLabel>Audio file</FormLabel>
                            <Button
                                as="label"
                                htmlFor="music-file-input"
                                leftIcon={<MdUploadFile/>}
                                variant="outline"
                                colorScheme="teal"
                                cursor="pointer"
                                isLoading={uploading}
                                loadingText="Uploading"
                                w="full"
                            >
                                {fileName ? "Choose a different file" : "Select audio file"}
                                <input id="music-file-input" type="file" accept="audio/*" hidden onChange={handleFile}/>
                            </Button>
                            {fileName && (
                                <HStack mt={2} color={objectKey ? "green.600" : "gray.500"} fontSize="sm">
                                    <Icon as={MdMusicNote}/>
                                    <Text noOfLines={1}>{fileName}</Text>
                                    {objectKey && <Badge colorScheme="green">uploaded</Badge>}
                                </HStack>
                            )}
                            {previewUrl && (
                                <Box mt={3}>
                                    <audio src={previewUrl} controls style={{width: "100%"}}/>
                                </Box>
                            )}
                        </FormControl>

                        <FormControl display="flex" alignItems="center">
                            <Switch
                                isChecked={form.enabled}
                                onChange={(e) => setForm((prev) => ({...prev, enabled: e.target.checked}))}
                                mr={3}
                                colorScheme="teal"
                            />
                            <FormLabel mb={0}>{form.enabled ? "Active in library" : "Inactive"}</FormLabel>
                        </FormControl>
                    </Stack>
                </ModalBody>
                <ModalFooter>
                    <Button variant="ghost" mr={3} onClick={onClose}>Cancel</Button>
                    <Button colorScheme="teal" onClick={handleSave} isLoading={saving} isDisabled={!objectKey || !form.name.trim()}>
                        Save
                    </Button>
                </ModalFooter>
            </ModalContent>
        </Modal>
    );
}

/* ----------------------------- page --------------------------- */

function MusicLibrary() {
    const toast = useToast();
    const {isOpen, onOpen, onClose} = useDisclosure();
    const cardBg = useColorModeValue("white", "gray.700");

    const [tracks, setTracks] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState("");
    const [currentId, setCurrentId] = useState(null);
    const [isPlaying, setIsPlaying] = useState(false);
    const [progress, setProgress] = useState(0);
    const [duration, setDuration] = useState(0);

    const audioRef = useRef(null);

    const fetchTracks = useCallback(async () => {
        setLoading(true);
        try {
            const response = await searchBellMusic({schoolId: null, name: ""}, 1, 100);
            if (response.status === 200) {
                setTracks(extractList(response.data));
            }
        } catch (e) {
            toast({title: "Could not load music", description: "Failed to fetch the bell music list.", status: "error", duration: 4000, isClosable: true});
        } finally {
            setLoading(false);
        }
    }, [toast]);

    useEffect(() => {
        fetchTracks();
    }, [fetchTracks]);

    const filtered = useMemo(() => {
        const term = searchTerm.trim().toLowerCase();
        if (!term) {
            return tracks;
        }
        return tracks.filter(
            (track) =>
                trackName(track).toLowerCase().includes(term) ||
                String(track?.type || "").toLowerCase().includes(term)
        );
    }, [tracks, searchTerm]);

    const stats = useMemo(() => {
        const active = tracks.filter((track) => Boolean(track?.enabled) || track?.status === "Y" || track?.status === "A").length;
        const types = new Set(tracks.map((track) => track?.type).filter(Boolean)).size;
        return {total: tracks.length, active, types};
    }, [tracks]);

    const stopPlayback = useCallback(() => {
        if (audioRef.current) {
            audioRef.current.pause();
            audioRef.current.currentTime = 0;
        }
        setIsPlaying(false);
        setCurrentId(null);
        setProgress(0);
        setDuration(0);
    }, []);

    const handlePlay = (track) => {
        const id = track.id ?? trackName(track);
        const url = trackUrl(track);
        if (!url) {
            toast({title: "No audio available", description: "This entry has no playable file.", status: "warning", duration: 3000, isClosable: true});
            return;
        }

        if (currentId === id) {
            if (isPlaying) {
                audioRef.current?.pause();
                setIsPlaying(false);
            } else {
                audioRef.current?.play().catch(() => {});
                setIsPlaying(true);
            }
            return;
        }

        if (audioRef.current) {
            audioRef.current.src = url;
            audioRef.current.play().then(() => setIsPlaying(true)).catch(() => {
                toast({title: "Unable to play", description: "The audio could not be played.", status: "error", duration: 3000, isClosable: true});
            });
        }
        setCurrentId(id);
    };

    const handleSeek = (value) => {
        if (audioRef.current && duration) {
            audioRef.current.currentTime = (value / 100) * duration;
            setProgress(value);
        }
    };

    const handleDelete = async (track) => {
        const id = track.id ?? track.bellMusicId;
        try {
            const response = await deleteBellMusic({id, bellMusicId: id});
            if (response.status === 200 || response.status === 201) {
                if (currentId === (track.id ?? trackName(track))) {
                    stopPlayback();
                }
                toast({title: "Music removed", status: "info", duration: 2000, isClosable: true});
                fetchTracks();
            } else {
                throw new Error("Unexpected status");
            }
        } catch (e) {
            toast({title: "Delete failed", description: "Could not remove this music.", status: "error", duration: 4000, isClosable: true});
        }
    };

    const currentTrack = useMemo(
        () => filtered.find((track) => (track.id ?? trackName(track)) === currentId) || tracks.find((track) => (track.id ?? trackName(track)) === currentId),
        [filtered, tracks, currentId]
    );

    return (
        <Flex direction="column" pt={{base: "110px", md: "80px"}} px={{base: "4", md: "8"}} pb="120px" bg="gray.50" minH="100vh">
            <Flex justify="space-between" align={{base: "flex-start", md: "center"}} direction={{base: "column", md: "row"}} gap={4} mb={6}>
                <HStack spacing={3}>
                    <Flex w="46px" h="46px" borderRadius="12px" bg="teal.500" color="white" align="center" justify="center">
                        <Icon as={MdLibraryMusic} boxSize="26px"/>
                    </Flex>
                    <Box>
                        <Text fontSize="2xl" fontWeight="bold">Music Library</Text>
                        <Text color="gray.600">Manage and play bell music for the digital bell system.</Text>
                    </Box>
                </HStack>
                <Button colorScheme="teal" leftIcon={<MdAdd/>} onClick={onOpen}>Add Music</Button>
            </Flex>

            <SimpleGrid columns={{base: 1, md: 3}} spacing={4} mb={6}>
                <Stat bg={cardBg} p={4} borderRadius="12px" boxShadow="sm">
                    <StatLabel color="gray.500">Total tracks</StatLabel>
                    <StatNumber>{stats.total}</StatNumber>
                </Stat>
                <Stat bg={cardBg} p={4} borderRadius="12px" boxShadow="sm">
                    <StatLabel color="gray.500">Active</StatLabel>
                    <StatNumber>{stats.active}</StatNumber>
                </Stat>
                <Stat bg={cardBg} p={4} borderRadius="12px" boxShadow="sm">
                    <StatLabel color="gray.500">Categories</StatLabel>
                    <StatNumber>{stats.types}</StatNumber>
                </Stat>
            </SimpleGrid>

            <Box mb={6} maxW="480px">
                <InputGroup boxShadow="sm" borderRadius="md" bg={cardBg}>
                    <InputLeftElement pointerEvents="none" children={<FaSearch color="gray"/>}/>
                    <Input
                        placeholder="Search music by name or type..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        borderRadius="md"
                    />
                </InputGroup>
            </Box>

            {loading ? (
                <Flex justify="center" py="80px" color="gray.400">Loading music…</Flex>
            ) : filtered.length === 0 ? (
                <Flex direction="column" align="center" justify="center" bg={cardBg} borderRadius="16px" boxShadow="sm" py="80px" textAlign="center">
                    <Box color="teal.400" fontSize="56px" mb={4}><MdLibraryMusic/></Box>
                    <Text fontSize="lg" fontWeight="bold">No music yet</Text>
                    <Text color="gray.500" mt={1}>Click “Add Music” to upload your first bell sound.</Text>
                </Flex>
            ) : (
                <Grid templateColumns={{base: "1fr", md: "1fr 1fr", lg: "1fr 1fr 1fr"}} gap={5}>
                    {filtered.map((track) => {
                        const id = track.id ?? trackName(track);
                        const active = currentId === id && isPlaying;
                        return (
                            <Box
                                key={id}
                                bg={cardBg}
                                p={5}
                                borderRadius="16px"
                                boxShadow="sm"
                                border="1px solid"
                                borderColor={active ? "teal.300" : "gray.100"}
                                transition="all 0.2s ease"
                                _hover={{transform: "translateY(-4px)", boxShadow: "lg"}}
                            >
                                <Flex justify="space-between" align="center" mb={4}>
                                    <Flex
                                        w="44px"
                                        h="44px"
                                        borderRadius="12px"
                                        align="center"
                                        justify="center"
                                        bg={active ? "teal.50" : "gray.100"}
                                        color={active ? "teal.500" : "gray.400"}
                                    >
                                        {active ? <Equalizer active/> : <Icon as={MdMusicNote} boxSize="22px"/>}
                                    </Flex>
                                    <IconButton
                                        aria-label="Delete music"
                                        icon={<MdDelete/>}
                                        size="sm"
                                        variant="ghost"
                                        colorScheme="red"
                                        onClick={() => handleDelete(track)}
                                    />
                                </Flex>

                                <Text fontWeight="bold" noOfLines={1}>{trackName(track)}</Text>
                                <Badge mt={1} colorScheme="purple" borderRadius="full">{typeLabel(track?.type)}</Badge>

                                <Button
                                    mt={4}
                                    w="full"
                                    leftIcon={active ? <MdPause/> : <MdPlayArrow/>}
                                    colorScheme={active ? "red" : "teal"}
                                    variant={active ? "solid" : "outline"}
                                    onClick={() => handlePlay(track)}
                                >
                                    {active ? "Pause" : "Play"}
                                </Button>
                            </Box>
                        );
                    })}
                </Grid>
            )}

            {/* Hidden audio engine */}
            <audio
                ref={audioRef}
                onTimeUpdate={(e) => {
                    const el = e.currentTarget;
                    setDuration(el.duration || 0);
                    setProgress(el.duration ? (el.currentTime / el.duration) * 100 : 0);
                }}
                onEnded={() => {
                    setIsPlaying(false);
                    setProgress(0);
                }}
            />

            {/* Now playing bar */}
            {currentTrack && (
                <Flex
                    position="fixed"
                    bottom="0"
                    left="0"
                    right="0"
                    bg={cardBg}
                    borderTop="1px solid"
                    borderColor="gray.200"
                    boxShadow="0 -4px 20px rgba(0,0,0,0.08)"
                    px={{base: 4, md: 8}}
                    py={3}
                    align="center"
                    gap={4}
                    zIndex={20}
                >
                    <IconButton
                        aria-label="Play or pause"
                        icon={isPlaying ? <MdPause/> : <MdPlayArrow/>}
                        colorScheme="teal"
                        isRound
                        onClick={() => handlePlay(currentTrack)}
                    />
                    <Box minW="0" flexShrink={0} w={{base: "120px", md: "200px"}}>
                        <Text fontWeight="bold" noOfLines={1}>{trackName(currentTrack)}</Text>
                        <Text fontSize="xs" color="gray.500">{typeLabel(currentTrack?.type)}</Text>
                    </Box>
                    <Slider flex="1" value={progress} onChange={handleSeek} focusThumbOnChange={false}>
                        <SliderTrack><SliderFilledTrack/></SliderTrack>
                        <SliderThumb/>
                    </Slider>
                    <Button size="sm" variant="ghost" onClick={stopPlayback}>Stop</Button>
                </Flex>
            )}

            <AddMusicModal isOpen={isOpen} onClose={onClose} onSaved={fetchTracks}/>
        </Flex>
    );
}

export default MusicLibrary;
